import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, Tag, Globe, Languages, Clock, ExternalLink, User, Briefcase, Award } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { EXPERT_CATEGORIES } from "@/i18n/translations";
import { useSeo } from "@/lib/seo";
import VerifiedBadge from "@/components/VerifiedBadge";

export default function ExpertDetail() {
  const { slug } = useParams();
  const { lang, L, t } = useLang();
  const navigate = useNavigate();
  const [o, setO] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => { api.get(`/expert-profiles/${slug}`).then(({ data }) => setO(data)).catch(() => setError(true)); }, [slug]);
  useSeo({ title: o ? o.name : "", description: o ? L(o, "summary") : "", lang, image: o?.photo, type: "profile" });

  if (error) return <div className="mx-auto max-w-3xl px-6 py-24 text-center text-anthracite/60">404</div>;
  if (!o) return <div className="mx-auto max-w-3xl px-6 py-24 text-center text-anthracite/40">…</div>;
  const v = o.verification || {};
  const Row = ({ icon: Icon, label, value }) => value ? (
    <div className="flex items-start gap-2 text-sm"><Icon className="mt-0.5 h-4 w-4 shrink-0 text-champagne" /><span className="text-anthracite/50">{label} :</span><span className="text-anthracite/80">{value}</span></div>
  ) : null;
  const S = ({ title, body }) => body ? (<div className="mb-8"><h3 className="mb-2 font-serif text-xl text-anthracite">{title}</h3><p className="whitespace-pre-line text-base leading-relaxed text-anthracite/80">{body}</p></div>) : null;

  return (
    <article className="mx-auto max-w-4xl px-6 py-12 lg:px-10">
      <button onClick={() => navigate("/experts")} className="mb-8 inline-flex items-center gap-2 text-sm text-anthracite/60 hover:text-champagne"><ArrowLeft className="h-4 w-4" /> {lang === "fr" ? "Retour aux experts" : "Back to experts"}</button>

      <div className="mb-6 flex flex-wrap items-center gap-5">
        {o.photo ? <img src={o.photo} alt="" className="h-20 w-20 shrink-0 rounded-full border border-tertiary object-cover" /> : <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full border border-tertiary bg-sand"><User className="h-8 w-8 text-anthracite/30" /></div>}
        <div>
          <div className="mb-2 flex flex-wrap items-center gap-3"><VerifiedBadge verified={o.rst_verified} size="lg" /></div>
          <h1 className="font-serif text-4xl leading-tight tracking-tight text-anthracite sm:text-5xl">{o.name}</h1>
          <p className="mt-1 text-anthracite/60">{L(o, "role")}{o.organization ? ` · ${o.organization}` : ""}</p>
        </div>
      </div>
      {L(o, "summary") && <p className="mb-8 text-lg leading-relaxed text-anthracite/70">{L(o, "summary")}</p>}

      {(o.expertises || []).length > 0 && (
        <div className="mb-8 flex flex-wrap gap-2" data-testid="expert-expertise-tags">
          {o.expertises.map((ex) => <span key={ex} className="rounded-full border border-champagne/30 bg-champagne/10 px-3 py-1 text-xs text-champagne">{EXPERT_CATEGORIES.find((x) => x.value === ex)?.[lang] || ex}</span>)}
        </div>
      )}

      <div className="mb-8 grid gap-3 border-y border-tertiary py-5 sm:grid-cols-2">
        <Row icon={Tag} label={t("sector")} value={o.sector || (o.sectors || []).join(", ")} />
        <Row icon={MapPin} label={lang === "fr" ? "Pays / marché" : "Country / market"} value={[o.region, o.country].filter(Boolean).join(", ")} />
        <Row icon={Globe} label={lang === "fr" ? "Zones" : "Zones"} value={(o.zones || []).join(", ")} />
        <Row icon={Languages} label={lang === "fr" ? "Langues" : "Languages"} value={(o.languages || []).join(", ")} />
        <Row icon={Award} label={lang === "fr" ? "Expérience" : "Experience"} value={o.experience_years ? `${o.experience_years} ${lang === "fr" ? "ans" : "years"}` : ""} />
      </div>

      <S title={lang === "fr" ? "Biographie" : "Biography"} body={L(o, "bio")} />
      <S title={lang === "fr" ? "Expérience" : "Experience"} body={L(o, "experience")} />
      <S title={lang === "fr" ? "Services" : "Services"} body={L(o, "services")} />
      <S title={lang === "fr" ? "Missions" : "Missions"} body={L(o, "missions")} />
      <S title={lang === "fr" ? "Références" : "References"} body={L(o, "references")} />

      <div className="mb-6" data-testid="verification-block" />

      <p className="mb-10 text-xs text-anthracite/50">{lang === "fr" ? "Cette expertise fait partie des compétences internes mobilisables par RSTI. Contactez RSTI pour l'associer à votre projet ou dossier." : "This expertise is part of RSTI's in-house competencies. Contact RSTI to bring it to your project or engagement."}</p>

      <div className="flex flex-col gap-3 border-t border-tertiary pt-8 sm:flex-row">
        <Link to={`/connect?category=experts&ref=${encodeURIComponent(o.name)}`} data-testid="cta-talk-rst" className="flex-1 bg-anthracite px-6 py-4 text-center text-sm font-medium text-ivory transition-colors hover:bg-black">{lang === "fr" ? "Parler à RSTI" : "Talk to RSTI"}</Link>
      </div>
    </article>
  );
}
